import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatPrice(value, currency = "USD") {
  if (value === null || value === undefined || value === "") return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(Number(value));
  } catch {
    return `${value}`;
  }
}

export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function downsizeImage(dataUrl, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const ratio = Math.min(1, maxDim / Math.max(width, height));
      const w = Math.round(width * ratio);
      const h = Math.round(height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(canvas.toDataURL("image/jpeg", quality));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export function categoryLabel(c) {
  const map = {
    inspiration: "Inspiration",
    site: "Site Photo",
    product: "Product",
    material: "Material",
    floorplan: "Floorplan",
    documents: "Documents",
    cad: "CAD",
  };
  return map[c] || prettify(c) || "Inspiration";
}

// Map of file extensions to library sub-categories. Anything not listed
// falls back to the current upload category (typically "inspiration").
const DOCUMENT_EXTENSIONS = new Set([
  "doc", "docx", "odt", "rtf",
  "xls", "xlsx", "ods", "csv",
  "ppt", "pptx", "odp", "key",
  "txt", "md",
]);
const CAD_EXTENSIONS = new Set([
  "dwg", "dxf", "dgn", "stp", "step",
  "iges", "igs", "skp", "rfa", "rvt", "3dm", "obj", "3ds",
]);
const DOCUMENT_MIMES = new Set([
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/rtf",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  "text/csv",
  "text/plain",
]);

/**
 * Infer the library category for an uploaded File.
 * Extension-first — browsers sometimes set file.type to image/vnd.dwg or
 * application/octet-stream which would otherwise miscategorise the file.
 *  - Office documents → "documents"
 *  - CAD drawings → "cad"
 *  - images / PDFs / unknown → `fallback` (typically the active tab category)
 */
export function getCategoryForFile(file, fallback = "inspiration") {
  if (!file) return fallback;
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  const mime = (file.type || "").toLowerCase();
  // 1. Extension wins (handles .dwg arriving as image/vnd.dwg, etc.)
  if (CAD_EXTENSIONS.has(ext)) return "cad";
  if (DOCUMENT_EXTENSIONS.has(ext)) return "documents";
  // 2. Real images & PDFs use the active library category
  if (mime.startsWith("image/") || mime === "application/pdf" || ext === "pdf") {
    return fallback;
  }
  // 3. Last-resort MIME fallback for documents
  if (DOCUMENT_MIMES.has(mime)) return "documents";
  // 4. Unknown binary → safer to file under documents than to pollute the active tab
  return "documents";
}

/**
 * Classify an upload into one of: 'image' | 'pdf' | 'doc'. Drives the
 * uploader's encoding (downsize / raw) and the inferred library category.
 * Returns { kind, category, mime } where:
 *   - kind: 'image' | 'pdf' | 'doc'
 *   - category: resolved library category (string)
 *   - mime: the MIME to send to the backend
 */
const VIDEO_EXTENSIONS = new Set([
  "mp4", "mov", "webm", "m4v", "avi", "mkv", "ogv",
]);

export function classifyUpload(file, fallback = "inspiration") {
  const category = getCategoryForFile(file, fallback);
  const name = (file?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  const rawMime = (file?.type || "").toLowerCase();
  // Anything routed to documents/cad is treated as a generic doc upload.
  if (category === "documents" || category === "cad") {
    return {
      kind: "doc",
      category,
      mime: rawMime || "application/octet-stream",
    };
  }
  if (rawMime === "application/pdf" || ext === "pdf") {
    return { kind: "pdf", category, mime: "application/pdf" };
  }
  if (rawMime.startsWith("video/") || VIDEO_EXTENSIONS.has(ext)) {
    const guessed = rawMime.startsWith("video/")
      ? rawMime
      : ext === "mov"
      ? "video/quicktime"
      : `video/${ext || "mp4"}`;
    // Videos use the active library category (typically inspiration)
    return { kind: "video", category: fallback, mime: guessed };
  }
  if (rawMime.startsWith("image/")) {
    return { kind: "image", category, mime: rawMime || "image/jpeg" };
  }
  // Fallback — treat as generic doc
  return {
    kind: "doc",
    category: "documents",
    mime: rawMime || "application/octet-stream",
  };
}

export function isVideo(image) {
  if (!image) return false;
  const m = (image.mime || "").toLowerCase();
  if (m.startsWith("video/")) return true;
  const fromData = getMimeFromDataUrl(image.data);
  if (fromData.startsWith("video/")) return true;
  const fn = (image.filename || "").toLowerCase();
  const ext = fn.includes(".") ? fn.split(".").pop() : "";
  return VIDEO_EXTENSIONS.has(ext);
}

/**
 * Whether the file is "previewable" (image or PDF). Other documents get
 * a generic-file thumbnail card.
 */
export function isPreviewableUpload(file) {
  if (!file) return false;
  const mime = (file.type || "").toLowerCase();
  return (
    mime.startsWith("image/") ||
    mime === "application/pdf" ||
    /\.pdf$/i.test(file.name || "")
  );
}

export function prettify(s) {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

export function getMimeFromDataUrl(s) {
  if (!s || typeof s !== "string") return "";
  const m = s.match(/^data:([^;]+);base64,/);
  return m ? m[1].toLowerCase() : "";
}

export function isPdf(image) {
  if (!image) return false;
  const m = (image.mime || "").toLowerCase();
  if (m === "application/pdf") return true;
  const fromData = getMimeFromDataUrl(image.data);
  if (fromData === "application/pdf") return true;
  return (image.filename || "").toLowerCase().endsWith(".pdf");
}

export function openInNewTab(image) {
  if (!image?.data) return;
  // For base64 PDFs, convert to blob URL so the browser can render it reliably
  try {
    const m = image.data.match(/^data:([^;]+);base64,(.+)$/);
    if (m) {
      const mime = m[1];
      const bin = atob(m[2]);
      const len = bin.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Revoke later to allow tab to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
  } catch (e) {
    /* fall through */
  }
  window.open(image.data, "_blank", "noopener");
}
